// Detect the memo output language from the user's *typed* text.
// Mirrors the backend heuristic (app/services/language.py) so the client and
// server agree. We detect on the client because the framed research question is
// always English — only the user's original words carry the language signal.

export type OutputLanguage = 'en' | 'hi' | 'ml';

export const LANGUAGE_LABELS: Record<OutputLanguage, string> = {
  en: 'English',
  hi: 'Hindi',
  ml: 'Malayalam',
};

const inRange = (c: string, lo: number, hi: number) => {
  const code = c.codePointAt(0)!;
  return code >= lo && code <= hi;
};

/**
 * Returns 'ml' | 'hi' | 'en'. A few stray Indic characters in an otherwise
 * English sentence must NOT flip the memo, so we switch to the dominant Indic
 * script only when it appears in real volume (>= 12 chars) or makes up a
 * meaningful share (>= 30%) of the alphabetic characters.
 */
export function detectLanguage(text: string | null | undefined): OutputLanguage {
  if (!text) return 'en';

  let ml = 0;
  let hi = 0;
  let letters = 0;
  for (const c of text) {
    if (inRange(c, 0x0d00, 0x0d7f)) ml++;
    else if (inRange(c, 0x0900, 0x097f)) hi++;
    if (/\p{L}/u.test(c)) letters++;
  }

  const indic = ml + hi;
  if (indic === 0) return 'en';
  const dominant: OutputLanguage = ml >= hi ? 'ml' : 'hi';

  if (indic >= 12) return dominant;
  if (letters && indic >= letters * 0.3) return dominant;
  return 'en';
}
